import './mapLogo.css';

import { useContext, useMemo, useRef, useState } from 'react';
import ZoomRadarLogo from '../../assets/img/ZoomRadarLogo.svg';
import parameterContext from '../../context/parameterContext';
import Modal from '../modal/Modal';
import { ReactComponent as WebIcon } from '../../assets/img/modal/web.svg';
import { MapLogoData, MapLogoModalProps } from './mapLogo.types';
import { getLogoPosition } from './mapLogo.helpers';

export const MapLogo = () => {
    const parameters = useContext(parameterContext);
    const logoRefs = {
        logo1: useRef<HTMLDivElement>(null),
        logo2: useRef<HTMLDivElement>(null),
        logo3: useRef<HTMLDivElement>(null)
    };
    const timeoutRef = useRef<NodeJS.Timeout>();
    const isHoveringModalRef = useRef(false); // To prevent modal from closing when user is hovering over it
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalProps, setModalProps] = useState<MapLogoModalProps>();

    const width = 'auto';
    const height = parameters.logo_height || parameters.custom_logo_height || 50; // Use logo_height if available (existing param), or use custom_logo_height (new param), fallback to default height
    const margin = parameters.custom_logo_margin || 36;
    const gap = parameters.custom_logo_gap || 26;
    const isMobile = window.innerWidth <= 768;

    // Determine logo position based on the params
    const position = useMemo(() => {
        if (isMobile) return 'bottom-right';
        if (parameters.top_left_logo) return 'top-left';
        if (parameters.custom_logo_position && ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(parameters.custom_logo_position)) {
            return parameters.custom_logo_position;
        }
        return 'bottom-right';
    }, [isMobile, parameters.top_left_logo, parameters.custom_logo_position]);
    // Get logo data based on the params
    const logos = useMemo<MapLogoData[]>(() => {
        const createLogoData = (logoPosition: number): MapLogoData | null => {
            const imageUrl =
                logoPosition === 1
                    ? parameters.custom_logo || parameters.custom_logo_1_image_url || ZoomRadarLogo
                    : parameters[`custom_logo_${logoPosition}_image_url`];
            if (!imageUrl) return null;

            let popupBehavior = parameters[`custom_logo_${logoPosition}_popup_behavior`];
            if (isMobile && popupBehavior === 'hover') {
                popupBehavior = 'click';
            }

            return {
                id: `logo${logoPosition}`,
                ref: logoRefs[`logo${logoPosition}` as keyof typeof logoRefs],
                imageUrl,
                popupBehavior,
                modalProps: {
                    popupBehavior,
                    imageUrl,
                    bannerImageUrl: parameters[`custom_logo_${logoPosition}_popup_banner_image_url`],
                    title: parameters[`custom_logo_${logoPosition}_popup_title`],
                    description: parameters[`custom_logo_${logoPosition}_popup_description`],
                    link: parameters[`custom_logo_${logoPosition}_popup_link`]
                }
            };
        };

        return [1, 2, 3].map(createLogoData).filter((logo): logo is MapLogoData => logo !== null);
    }, [parameters, isMobile, logoRefs]);

    const startCloseTimer = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            if (!isHoveringModalRef.current) {
                setIsModalOpen(false);
            }
        }, 1000);
    };

    const handleClick = (logo: MapLogoData) => {
        // Clear timeout to prevent multiple timeouts interfering
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }

        const modalProps = {
            ...logo.modalProps,
            position: getLogoPosition(position, logo.ref)
        };
        if ((modalProps.popupBehavior === 'link' || modalProps.popupBehavior === 'hover') && modalProps.link) {
            // Open to new tab for link and hover
            window.open(modalProps.link, '_blank');
            return;
        }
        if (modalProps.popupBehavior === 'click') {
            // Open in modal for click
            setModalProps(modalProps);
            setIsModalOpen(true);
            return;
        }
    };

    const handleLogoMouseOver = (logo: MapLogoData) => {
        const modalProps = {
            ...logo.modalProps,
            position: getLogoPosition(position, logo.ref)
        };
        if (modalProps?.popupBehavior === 'hover' || modalProps?.popupBehavior === 'link') {
            // Clear timeout to prevent multiple timeouts interfering
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }
            isHoveringModalRef.current = false;

            // Open in modal for hover
            setModalProps(modalProps);
            setIsModalOpen(true);
        }
    };

    const handleModalMouseOver = () => {
        isHoveringModalRef.current = true;
    };

    const handleMouseOut = () => {
        // Close modal when mouse leaves the logo (while not hovering over the modal)
        if (modalProps?.popupBehavior === 'hover' || modalProps?.popupBehavior === 'link') {
            isHoveringModalRef.current = false;
            startCloseTimer();
        }
    };

    return (
        <>
            {/* Modal when logo is clicked or hovered */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                overlayClassName="map-logo-modal-overlay"
                overlayStyle={{
                    top: modalProps?.position.top,
                    left: modalProps?.position.left,
                    bottom: modalProps?.position.bottom,
                    right: modalProps?.position.right,
                    ...(isMobile && {
                        maxHeight: `calc(100dvh - ${height + margin + gap + 10}px)`
                    })
                }}
                className="map-logo-modal"
                style={{
                    ...(!isMobile && {
                        maxHeight: `calc(100dvh - ${height + margin + gap + 50}px)`
                    })
                }}
                onMouseEnter={handleModalMouseOver}
                onMouseLeave={handleMouseOut}
            >
                {modalProps?.bannerImageUrl && <img src={modalProps.bannerImageUrl} alt="Sponsor Banner" className="map-logo-modal__image" />}
                {modalProps?.imageUrl && <img src={modalProps.imageUrl} alt={'Company Logo'} className="map-logo-modal__logo" />}

                {modalProps?.title && <h2 className="map-logo-modal__title">{modalProps.title}</h2>}
                {modalProps?.description && <p className="map-logo-modal__description">{modalProps.description}</p>}
                {modalProps?.link && (
                    <a href={modalProps.link} className="map-logo-modal__link" target="_blank" rel="noopener noreferrer" aria-label="Open sponsor link">
                        <WebIcon aria-hidden="true" />
                        {modalProps.link}
                    </a>
                )}
            </Modal>

            {/* Logo(s) */}
            <div className={'logo-icon ' + position} style={{ gap, margin }} role="group" aria-label="Company logos">
                {logos.map((logo) => (
                    <div
                        key={logo.id}
                        ref={logo.ref}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleClick(logo)}
                        onMouseOver={() => handleLogoMouseOver(logo)}
                        onMouseOut={handleMouseOut}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleClick(logo);
                            }
                        }}
                        aria-label={`Company logo ${logo.id.slice(-1)}`}
                        aria-haspopup={logo.popupBehavior === 'click' || logo.popupBehavior === 'hover' || logo.popupBehavior === 'link'}
                    >
                        <img src={logo.imageUrl} alt={`Logo ${logo.id.slice(-1)}`} style={{ width, height }} />
                    </div>
                ))}
            </div>
        </>
    );
};
